import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowPathIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import StatusBadge from '../components/StatusBadge';
import Money from '../components/Money';
import EmptyState from '../components/EmptyState';
import { fetchBuyerQuotations, acceptBuyerQuotation, rejectBuyerQuotation } from '../api/marketplaceApi';

const BuyerQuotations: React.FC = () => {
  const [quotations, setQuotations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [actionId, setActionId] = useState<number | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, any> = {};
      if (statusFilter) params.status = statusFilter;
      const res = await fetchBuyerQuotations(params);
      setQuotations(res.quotations || res.items || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleAccept = async (id: number) => {
    setActionId(id);
    try {
      await acceptBuyerQuotation(id);
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to accept');
    } finally {
      setActionId(null);
    }
  };

  const handleReject = async (id: number) => {
    const reason = window.prompt('Reason for rejection (optional):');
    setActionId(id);
    try {
      await rejectBuyerQuotation(id, reason || undefined);
      load();
    } catch (err: any) {
      alert(err.message || 'Failed to reject');
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Quotations</h2>
          <p className="text-sm text-gray-500 mt-0.5">Review and manage supplier quotations</p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg"
        >
          <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
        >
          <option value="">All Status</option>
          <option value="submitted">Submitted</option>
          <option value="revised">Revised</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="withdrawn">Withdrawn</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between">
          <p className="text-sm text-red-700">{error}</p>
          <button onClick={load} className="text-sm font-medium text-red-600 hover:underline">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <ArrowPathIcon className="w-6 h-6 text-gray-400 animate-spin" />
        </div>
      ) : quotations.length === 0 ? (
        <EmptyState
          icon={<ClipboardDocumentListIcon className="w-12 h-12" />}
          title="No quotations yet"
          description="Quotations will appear here when suppliers respond to your RFQs."
        />
      ) : (
        <div className="space-y-3">
          {quotations.map((q: any) => (
            <div
              key={q.id}
              className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/marketplace/buyer/quotations/${q.id}`)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-1">
                    <h3 className="text-sm font-semibold text-gray-900">{q.quotation_number}</h3>
                    <StatusBadge status={q.status} size="sm" />
                  </div>
                  <p className="text-xs text-gray-500">
                    From: {q.supplier_name || `Supplier #${q.supplier_tenant_id}`}
                  </p>
                  {q.rfq_title && (
                    <p className="text-xs text-gray-400 mt-0.5">RFQ: {q.rfq_title}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    {q.delivery_lead_days && <span>Lead time: {q.delivery_lead_days} days</span>}
                    {q.validity_days && <span>Valid: {q.validity_days} days</span>}
                    <span>Submitted: {q.submitted_at ? new Date(q.submitted_at).toLocaleDateString() : new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-900">
                    <Money amount={q.total_amount} currency={q.currency} />
                  </p>
                  {q.status === 'submitted' && (
                    <div className="flex gap-2 mt-2" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => handleReject(q.id)}
                        disabled={actionId === q.id}
                        className="px-3 py-1.5 text-xs font-medium border border-red-200 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                      >
                        Reject
                      </button>
                      <button
                        onClick={() => handleAccept(q.id)}
                        disabled={actionId === q.id}
                        className="px-3 py-1.5 text-xs font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {actionId === q.id ? '...' : 'Accept'}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BuyerQuotations;
